import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { ModalComponent } from '../../shared/modal/modal.component';
import { InputNumberModule } from 'primeng/inputnumber';
import _ from 'lodash';
import { CustomFormControl } from '../../shared/shared.model';
import { DateMaskDirective } from '../../shared/directive/date-mask.directive';
import { CleanStepPanelAccessibilityDirective } from '../../order-info/accesibility.directive';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-subjects',
  imports: [InputTextModule, DatePickerModule, CommonModule, CleanStepPanelAccessibilityDirective,
    ReactiveFormsModule, PanelModule, CardModule, ButtonModule, ModalComponent,InputNumberModule, DateMaskDirective],
  templateUrl: './subjects.component.html',
  styleUrl: './subjects.component.scss'
})
export class SubjectsComponent implements OnInit, AfterViewInit {
  @ViewChild('stepHeading') stepHeading!: ElementRef;
  @ViewChildren('subjectHeaders') subjectHeaders!: QueryList<ElementRef>;

  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef, private messageService: MessageService) { }

  @Input() formData: any;
  @Output() formChanged = new EventEmitter<void>();
  @Input() orderType: any;
  @Input() editOrder: any;
  formGroup!: FormGroup;
  showDeleteModal: boolean = false;
  itemToDelete: any;
  maxDate: Date = new Date();

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      'subjects': new FormArray([this.createSubjectFormGroup()])
    });
    if(this.formData) {
      this.initializeFormData();
    }

    this.formGroup.valueChanges.subscribe(()=>{
      this.formChanged.emit();
    });
  }

  // add focus to the top header 
  ngAfterViewInit() {
    setTimeout(() => {
      this.stepHeading.nativeElement.focus();
    });
  }

  /**
   * Gets the subjects form array.
   * @returns The FormArray of subjects.
   */
  get subjects(): FormArray {
    return this.formGroup.get('subjects') as FormArray;
  }

  /**
   * Adds a new subject to the subjects form array.
   */
  addNewSubject(): void {
    this.subjects.push(this.createSubjectFormGroup());

    // add focus to newly added subject
    this.cdr.detectChanges();
    setTimeout(() => {
      const headersArray = this.subjectHeaders.toArray();
      const newHeader = headersArray[headersArray.length - 1];
      newHeader.nativeElement.focus();
    });
  }

  /**
   * Removes a subject from the subjects form array.
   * @param index The index of the subject to remove.
   */
  removeSubject(index: number) {
    this.showDeleteModal = true;
    this.itemToDelete = index;
  }

  createSubjectFormGroup(item?: any): FormGroup {
    return new FormGroup({
      'recordSubjectName': new CustomFormControl(item ? item.recordSubjectName : null, 'Record Subject Name', [Validators.required]),
      'alias': new FormControl(item ? item.alias : null),
      'dateOfBirth': new CustomFormControl(item ? item.dateOfBirth : null, 'Date of Birth', [Validators.required]),
      'ssn': new FormControl(item ? item.ssn : null),
      'dateOfDeath': new CustomFormControl(item ? item.dateOfDeath : null, 'Date of Death'),
      'isNew': new FormControl(item ? item.isNew : true),
      'keys': new FormControl(item ? item.keys : null)
    });
  }

  /**
 * Highlights a control for validation.
 */
  isError(controlId: any, control?: any) {
    let formControl = this.formGroup.get(controlId);
    if (!formControl)
      formControl = control.get(controlId);
    return formControl ? (formControl.invalid && (formControl.dirty || formControl.touched)) : false;
  }

  delete() {
    if (this.itemToDelete >= 0) {
      this.subjects.removeAt(this.itemToDelete);
      this.itemToDelete = null;
      this.showDeleteModal = false;
      this.messageService.add({
            severity: 'success',
            summary: 'SUCCESS',
            detail: `Subject successfully deleted.`,
            life: 3000,
          });
    }
  }

  initializeFormData() {
    if (this.formData?.subjects) {
      this.subjects.clear();
      _.forEach(this.formData.subjects, subject => {
        this.subjects.push(this.createSubjectFormGroup(subject));
      })
    }

  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

    // Allow control keys
    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Block anything not a digit
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onSsnChange(event: any, index: number) {
    let ssnControl = this.subjects.at(index).get('ssn');
    const value = (event.target as HTMLInputElement).value;
    if (value && _.toString(value).length > 0) {
      ssnControl?.setValidators([Validators.pattern(/^\d{4}$/)]); 
    } else {
      ssnControl?.clearValidators(); 
    }
    // ssnControl?.markAsTouched();
    ssnControl?.updateValueAndValidity(); 
  }

  onDateChange(event: any, index: number, dateType: string) {
    const rawValue = event.target.value;
    const inputDate = new Date(rawValue);
    const subjectFormGroup = (this.formGroup.get('subjects') as FormArray).at(index);
      
    if (!isNaN(inputDate.getTime()) && inputDate > this.maxDate) {
      if (dateType === 'DOB') {
        subjectFormGroup.get('dateOfBirth')?.setErrors({ maxDateExceeded: true });  
      } else {
        subjectFormGroup.get('dateOfDeath')?.setErrors({ maxDateExceeded: true });  
      }
    }
  }
}

